import { IFacility } from "@/api/facilities";
import useAnalytics from "@/hooks/useAnalytics";
import { useDeleteFacilities } from "@/hooks/useFacilities";
import useTranslation from "@/hooks/useTranslation";
import { ExclamationCircleFilled } from "@ant-design/icons";
import { message, Popconfirm, Tooltip } from "antd";
import { ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import TooltipLink from "@/components/TooltipLink/TooltipLink";

export interface DeleteFacilityActionProps {
  children: ReactNode;
  facilities: IFacility[];
  onSucces?: (deletedIds: number[]) => void;
  deselectUndeletable?: () => void;
  deleteAll?: boolean;
}

export default function DeleteFacilityAction({
  children,
  facilities,
  onSucces,
  deselectUndeletable,
  deleteAll,
}: DeleteFacilityActionProps) {
  const [searchParams] = useSearchParams();
  const deleteFacilities = useDeleteFacilities();

  const productText =
    deleteAll === true
      ? "all unrelated facilities"
      : facilities.length === 1
        ? "facility"
        : `${facilities.length} facilities`;

  const disabled = facilities.some((x) => !x.deletable);
  const undeletableCount = facilities.filter((x) => !x.deletable).length;
  const translation = useTranslation();
  const analytics = useAnalytics();

  return (
    <Popconfirm
      title={`Delete ${productText}`}
      description={
        <>
          {deleteAll ? (
            <p>
              This action deletes all facilities that are not referenced in your purchase or sales
              orders. If you want to delete all facilities, first delete your purchase and sales
              orders.
            </p>
          ) : (
            ""
          )}
          <p>Deleting a facility also deletes:</p>
          <ul>
            <li>related utility contracts</li>
          </ul>
          <p style={{ color: "#F5222D" }}>This action can not be undone.</p>
        </>
      }
      onConfirm={() =>
        deleteFacilities
          .mutateAsync({ facilityIds: facilities.map((x) => x.id), deleteAll })
          .then((result) => {
            if (result.status === 200) {
              analytics.track(
                `${result.data.deleted_ids.length > 1 ? "Facilities" : "Facility"} Deleted`
              );
              message.success(
                `${
                  result.data.deleted_ids.length === 1
                    ? "1 facility"
                    : result.data.deleted_ids.length + " facilities"
                } removed.`
              );
              onSucces && onSucces(result.data.deleted_ids);
            }
          })
          .catch(() => message.error(translation.errors.general))
      }
      onCancel={() => {}}
      icon={<ExclamationCircleFilled style={{ color: "#F5222D" }} />}
      okText={`Delete ${productText}`}
      cancelText="Cancel"
      placement="bottom"
      okType="danger"
      okButtonProps={{ loading: deleteFacilities.isLoading }}
      disabled={disabled}
      overlayStyle={{ width: 500 }}
    >
      <Tooltip
        placement="bottom"
        title={
          disabled &&
          (facilities.length === 1 ? (
            <p>
              This facility can not be deleted as it is referenced in one of your purchase or sales
              orders. Remove the purchase and sales orders referencing this facility to remove this
              facility.
            </p>
          ) : (
            <p>
              {undeletableCount} of the facilities you selected are referenced in a purchase or
              sales order.{" "}
              {deselectUndeletable ? (
                <>
                  <TooltipLink
                    to={{ pathname: "", search: `?${searchParams.toString()}` }}
                    replace={true}
                    onClick={() => deselectUndeletable()}
                  >
                    Click here
                  </TooltipLink>{" "}
                  to deselect them or remove the related purchase and sales orders to continue.
                </>
              ) : (
                <>Remove the related purchase and sales orders to continue.</>
              )}
            </p>
          ))
        }
      >
        {children}
      </Tooltip>
    </Popconfirm>
  );
}
